import React, { useState, useEffect } from 'react';
import './GCodeSettingsPopup.css';
import { api } from '../services/api';
import { useTranslation } from 'react-i18next';

// Define nozzle options
const NOZZLE_OPTIONS = [
  { value: '0.4', label: '0.4mm' },
  { value: '0.6', label: '0.6mm' }
];

// Material variant interface
interface MaterialVariant {
  id: string;
  name: string;
}

// Define material types with proper typing
interface MaterialTypes {
  [key: string]: MaterialVariant[];
}

// Define hardcoded material types
const MATERIAL_TYPES: MaterialTypes = {
  'PLA': [{ id: 'pla_standard', name: 'Standard' }],
  'PETG': [{ id: 'petg_standard', name: 'Standard' }],
  'ABS': [{ id: 'abs_standard', name: 'Standard' }],
  'ASA': [{ id: 'asa_standard', name: 'Standard' }],
  'TPU': [{ id: 'tpu_standard', name: 'Standard' }]
};

interface Printer {
  id: string;
  name: string;
  variants: string[];
  technology: string;
  family: string;
}

interface PrinterManufacturers {
  [key: string]: Printer[];
}

export interface GCodeSettings {
  printer: string;
  model: string;
  nozzle: string;
  material: string;
  material_variant: string;
}

interface GCodeSettingsPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (settings: GCodeSettings) => void;
  onBack?: () => void;
}

export function GCodeSettingsPopup({ isOpen, onClose, onSubmit, onBack }: GCodeSettingsPopupProps) {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<GCodeSettings>({
    printer: '',  // Manufacturer
    model: '',    // Specific printer model
    nozzle: '0.4',
    material: 'PLA',  // Default material type
    material_variant: 'pla_standard', // Default variant
  });

  const [manufacturers, setManufacturers] = useState<PrinterManufacturers>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch printer manufacturers when component mounts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const printersResponse = await api.printers.getManufacturers();
        
        setManufacturers(printersResponse);
        
        // Set default values if none selected
        if (!settings.printer && Object.keys(printersResponse).length > 0) {
          const firstManufacturer = Object.keys(printersResponse)[0];
          const firstModel = printersResponse[firstManufacturer][0]?.id || '';
          
          setSettings(prev => ({
            ...prev,
            printer: firstManufacturer,
            model: firstModel
          }));
        }
      } catch (err) {
        setError(t('gcodeSettings.error'));
        console.error('Error loading printer configurations:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [t]);

  if (!isOpen) return null;

  const selectedManufacturerPrinters = manufacturers[settings.printer] || [];
  
  // Create translated material types
  const translatedMaterialTypes = Object.keys(MATERIAL_TYPES).reduce((acc, material) => {
    acc[material] = MATERIAL_TYPES[material].map(variant => ({
      ...variant,
      name: t('gcodeSettings.materials.standard')
    }));
    return acc;
  }, {} as MaterialTypes);
  
  const selectedMaterialVariants = translatedMaterialTypes[settings.material] || [];
  
  return (
    <div className={`modal-overlay ${isOpen ? 'with-intent-popup' : ''}`}>
      <div className="orientation-popup">
        <div className="modal-header">
          <h2>{t('gcodeSettings.title')}</h2>
          <button 
            className="close-button" 
            onClick={onClose}
            aria-label={t('gcodeSettings.close')}
          >
            <svg width="10" height="10" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M13 1L1 13M1 1L13 13" stroke="#cccccc" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        {error ? (
          <div className="error-message">{error}</div>
        ) : loading ? (
          <div className="loading-message">{t('gcodeSettings.loading')}</div>
        ) : (
          <div className="modal-content">
            <div className="form-group">
              <label htmlFor="manufacturer-select">{t('gcodeSettings.labels.manufacturer')}</label>
              <select 
                id="manufacturer-select" 
                value={settings.printer}
                onChange={(e) => {
                  const manufacturer = e.target.value;
                  const firstModel = manufacturers[manufacturer]?.[0]?.id || '';
                  setSettings({
                    ...settings,
                    printer: manufacturer,
                    model: firstModel
                  });
                }}
              >
                {Object.keys(manufacturers).map(manufacturer => (
                  <option key={manufacturer} value={manufacturer}>
                    {manufacturer}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="model-select">{t('gcodeSettings.labels.printerModel')}</label>
              <select 
                id="model-select" 
                value={settings.model}
                onChange={(e) => setSettings({...settings, model: e.target.value})}
              >
                {selectedManufacturerPrinters.map(printer => (
                  <option key={printer.id} value={printer.id}>
                    {printer.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="nozzle-select">{t('gcodeSettings.labels.nozzleDiameter')}</label>
              <select 
                id="nozzle-select" 
                value={settings.nozzle}
                onChange={(e) => setSettings({...settings, nozzle: e.target.value})}
              >
                {NOZZLE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-group">
              <label htmlFor="material-select">{t('gcodeSettings.labels.materialType')}</label>
              <select 
                id="material-select" 
                value={settings.material}
                onChange={(e) => {
                  const material = e.target.value;
                  const firstVariant = translatedMaterialTypes[material]?.[0]?.id || '';
                  setSettings({
                    ...settings,
                    material: material,
                    material_variant: firstVariant
                  });
                }}
              >
                {Object.keys(translatedMaterialTypes).map(material => (
                  <option key={material} value={material}>
                    {material}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="material-variant-select">{t('gcodeSettings.labels.materialProfile')}</label>
              <select 
                id="material-variant-select" 
                value={settings.material_variant}
                onChange={(e) => setSettings({...settings, material_variant: e.target.value})}
              >
                {selectedMaterialVariants.map(variant => (
                  <option key={variant.id} value={variant.id}>
                    {variant.name}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="popup-buttons">
              {onBack && (
                <button className="back-button" onClick={onBack}>
                  Back
                </button>
              )}
              <button 
                className="apply-button" 
                onClick={() => onSubmit(settings)}
              >
                {t('gcodeSettings.buttons.generate')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 